import React from 'react';
import { AVATAR_OPTIONS, DEFAULT_AVATAR, getAvatarById, isAvatarUnlocked, MILESTONE_AVATARS } from '@/data/avatars';
import { Lock } from 'lucide-react';

/**
 * PixelSpriteAvatar - Renders exact pixel sprites for Dino and Llama
 * Matches EXACTLY the timer animation sprites
 */
const PixelSpriteAvatar = ({ pixels, color, size }) => {
  const pixelSize = size / Math.max(pixels.length, pixels[0]?.length || 1) * 0.85;
  const height = pixels.length;
  const width = pixels[0]?.length || 0;
  
  return (
    <svg 
      width={size * 0.85} 
      height={size * 0.85} 
      viewBox={`0 0 ${width} ${height}`}
      style={{ imageRendering: 'pixelated' }}
    >
      {pixels.map((row, y) => 
        row.map((pixel, x) => 
          pixel === 1 && (
            <rect
              key={`${x}-${y}`}
              x={x}
              y={y}
              width={1}
              height={1}
              fill={color}
            />
          )
        )
      )}
    </svg>
  );
};

/**
 * PixelAvatar - Renders high-quality pixel art animal avatars
 * - Dino and Llama use exact pixel sprites from the timer
 * - Other animals use cloud-hosted PNG images
 * - All avatars have TRANSPARENT backgrounds
 */
export const PixelAvatar = ({ 
  avatarId = 'dino', 
  size = 64,
  className = '',
  showBorder = true 
}) => {
  const avatar = getAvatarById(avatarId);
  
  // Transparent background with optional border
  const containerStyle = showBorder ? 
    'rounded-full border-2 border-gray-200 dark:border-gray-700 overflow-hidden' : 
    'rounded-full overflow-hidden';
  
  // For pixel sprite avatars (dino and llama), render inline
  if (avatar?.isPixelSprite && avatar.pixels && avatar.color) {
    return (
      <div 
        className={`${className} ${containerStyle}`}
        style={{ 
          width: size, 
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <PixelSpriteAvatar 
          pixels={avatar.pixels} 
          color={avatar.color} 
          size={size}
        />
      </div>
    );
  }
  
  // For PNG image avatars - transparent background
  return (
    <div 
      className={`${className} ${containerStyle}`}
      style={{ 
        width: size, 
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
      }}
    >
      <img 
        src={avatar?.imageUrl}
        alt={avatar?.name || avatarId}
        style={{ 
          width: size * 0.85, 
          height: size * 0.85,
          objectFit: 'contain',
          imageRendering: 'pixelated',
        }}
        loading="lazy"
      />
    </div>
  );
};

/**
 * AvatarSelector - Grid of all available avatars to choose from
 * Shows locked milestone avatars with lock icon and unlock requirements
 */
export const AvatarSelector = ({ 
  selectedId, 
  onSelect, 
  size = 56, 
  isSpanish = false,
  user = null  // Pass user to check milestone unlocks
}) => {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 p-2">
      {AVATAR_OPTIONS.map((avatar) => {
        const isUnlocked = isAvatarUnlocked(avatar.id, user);
        const milestone = avatar.isMilestone ? MILESTONE_AVATARS[avatar.milestoneId] : null;
        
        return (
          <button
            key={avatar.id}
            onClick={() => isUnlocked && onSelect(avatar.id)}
            disabled={!isUnlocked}
            className={`relative flex flex-col items-center p-1.5 rounded-lg border-2 transition-all ${
              !isUnlocked 
                ? 'opacity-50 cursor-not-allowed border-transparent' 
                : selectedId === avatar.id 
                  ? 'border-[#00E5CC] bg-[#00E5CC]/10' 
                  : 'border-transparent hover:border-muted hover:bg-muted/50'
            }`}
            title={
              isUnlocked 
                ? (isSpanish ? avatar.nameEs : avatar.name)
                : (isSpanish ? milestone?.descriptionEs : milestone?.descriptionEn)
            }
            data-testid={`avatar-option-${avatar.id}`}
          >
            {/* Lock overlay for locked avatars */}
            {!isUnlocked && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg z-10">
                <Lock className="w-5 h-5 text-white/80" />
              </div>
            )}
            
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <PixelAvatar avatarId={avatar.id} size={48} showBorder={false} />
            </div>
            <span className={`text-[9px] sm:text-[10px] mt-1 text-center leading-tight w-full ${
              avatar.isMilestone && isUnlocked ? 'text-yellow-500 font-medium' : 'text-muted-foreground'
            }`} style={{ 
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
              minHeight: '28px',
            }}>
              {isSpanish ? avatar.nameEs : avatar.name}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * ColorSelector - Deprecated (colors are now fixed per animal image)
 */
export const ColorSelector = ({ isSpanish = false }) => {
  return (
    <div className="text-sm text-muted-foreground italic p-2 bg-muted/30 rounded">
      {isSpanish 
        ? '✨ Cada animal tiene sus colores naturales únicos'
        : '✨ Each animal has its own unique natural colors'}
    </div>
  );
};

/**
 * UserAvatar - Display a user's avatar
 */
export const UserAvatar = ({ user, size = 40, className = '' }) => {
  const avatarId = user?.avatar || DEFAULT_AVATAR;
  
  return (
    <PixelAvatar 
      avatarId={avatarId} 
      size={size} 
      className={className}
      showBorder={true}
    />
  );
};

export default PixelAvatar;
